import { useNavigate } from "@entry/navigation";
import styled from "styled-components";

import {
  FUTURE_MONEY_FORM_ADV_PART_2A_URL,
  FUTURE_MONEY_PRIVACY_POLICY_URL,
  FUTURE_MONEY_TERMS_OF_SERVICE_URL,
} from "@constants";
import { AnalyticsEvent, Routes, StorageKey } from "@ctypes";
import { EntryBody, EntryCta, EntryLink, EntryTitle } from "@entry/ui";
import { useEntryAnimation } from "@entry/useEntryAnimation";
import { Translations, i18n } from "@i18n";
import { Analytics } from "@services";
import { Screen, ScreenColumn, ScreenCta, useCtaSpacer, useEntryPageAnalytics } from "./shared";

const Prompt = styled(EntryTitle)`
  margin-top: 44px;
`;

const Terms = styled(EntryBody)`
  margin-top: 22px;
  max-width: 400px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.65);
`;

export const GetStartedScreen = () => {
  const navigate = useNavigate();

  useEntryPageAnalytics(Routes.GetStarted);

  const { contentStyle, formStyle, ctaStyle, animateOut } = useEntryAnimation({
    variant: "sequenced",
  });
  const { ctaRef, ctaSpacerHeight } = useCtaSpacer();

  const handleContinue = () => {
    Analytics.track(AnalyticsEvent.EntryGetStartedPrimaryCTAPressed);

    try {
      // A phone-first signup reaches this screen from the missing-field router rather than from
      // a tap on "get started", and that router would keep returning it. Mobile keeps the same
      // flag in its persisted intake as hasSeenOnboardingIntroduction.
      localStorage.setItem(StorageKey.HasSeenGetStarted, "true");
    } catch {
      // Storage unavailable (private mode/quota) - worst case the welcome shows twice.
    }

    animateOut(() => navigate(Routes.EntryName));
  };

  return (
    <Screen>
      <ScreenColumn>
        <div style={contentStyle}>
          <EntryTitle $size={36}>
            <b>{i18n.t(Translations.EntryGetStartedGreetingBold)}</b>
            <br />
            {i18n.t(Translations.EntryGetStartedGreetingBody)}
          </EntryTitle>
        </div>

        <div style={formStyle}>
          <Prompt as="p" $size={32}>
            {i18n.t(Translations.EntryGetStartedPrompt)}
          </Prompt>
        </div>

        <div aria-hidden="true" style={{ height: ctaSpacerHeight, flexShrink: 0 }} />
        <ScreenCta ref={ctaRef} style={ctaStyle}>
          <EntryCta type="button" $variant="green" onClick={handleContinue}>
            {i18n.t(Translations.EntryGetStartedPrimaryCTA)}
          </EntryCta>
          <Terms $align="center">
            {i18n.t(Translations.EntryGetStartedTermsPrefix)}
            <EntryLink
              href={FUTURE_MONEY_TERMS_OF_SERVICE_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {i18n.t(Translations.EntryGetStartedTermsConditions)}
            </EntryLink>
            {i18n.t(Translations.EntryGetStartedTermsAnd)}
            <EntryLink
              href={FUTURE_MONEY_PRIVACY_POLICY_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {i18n.t(Translations.EntryGetStartedTermsPrivacy)}
            </EntryLink>
            {i18n.t(Translations.EntryGetStartedTermsAnd2)}
            <EntryLink
              href={FUTURE_MONEY_FORM_ADV_PART_2A_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {i18n.t(Translations.EntryGetStartedTermsAdv)}
            </EntryLink>
          </Terms>
        </ScreenCta>
      </ScreenColumn>
    </Screen>
  );
};
